import React, { Dispatch, SetStateAction, useState } from 'react';
import { format } from 'date-fns';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { SettingsDA } from '../../services/admin/settings.service';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Deadline } from '../../models/deadline.model';
import { legendIconValidInvalidRestart, legendIconInvalid, legendValid, legendInvalid } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLegend from '../element/Legend';
import AppInputDeadlineType from '../element/InputDeadlineType';

export interface AppModalSettingsCreateDeadlineProps {
  companyForUser: Company | null,
  setValidUntil: Dispatch<SetStateAction<{value: string, valid: boolean}>>,
  setPaymentDeadline: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>
  loadSettingsForCompany: Function
};

let errorResponse: Error;

const AppModalSettingsCreateDeadline: React.FunctionComponent<AppModalSettingsCreateDeadlineProps> = ({companyForUser, setValidUntil, setPaymentDeadline, loadSettingsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [type, setType] = useState({value: '', valid: false})
  const [quantity, setQuantity] = useState({value: '', valid: false})

  const submitSettingsParameterCreate = async () => {
    setLoadIndicator('on')

    if (companyForUser && type.valid && quantity.valid) {
      await SettingsDA.postSettingPaymentDeadline(companyForUser.id, type.value, quantity.value).then( (response) => {
        if (response.status === 201 || response.status === 200) {
          let tempDeadline = response.data as Deadline
          let tempDate: Date = new Date()

          switch (tempDeadline.type) {
            case 'day':
              tempDate.setDate(tempDate.getDate() + tempDeadline.quantity)
              break;
            case 'week':
              tempDate.setDate(tempDate.getDate() + tempDeadline.quantity * 7)
              break;
            case 'month':
              tempDate.setMonth(tempDate.getMonth() + tempDeadline.quantity)
              break;  
            case 'year':
              tempDate.setFullYear(tempDate.getFullYear() + tempDeadline.quantity)
              break;
          }

          loadSettingsForCompany(companyForUser.id)
          setValidUntil({value: format(tempDate, 'yyyy-MM-dd'), valid: true})
          setPaymentDeadline({value: tempDeadline.id, object: tempDeadline, valid: true})
          legendValid('legend-valid-until-required')

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedCompanyParameters,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalSettingUnitCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!type.value.length) legendInvalid('modal-settings-create-payment-deadline-legend-type-required')
        if (!quantity.value.length) legendIconInvalid('modal-settings-create-payment-deadline-input-quantity', 'modal-settings-create-payment-deadline-legend-quantity-required')
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeQuantity = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setQuantity({...quantity, value: event.target.value})
  }

  const validateQuantity = () => {
    evaluateLegendRequiredIcon(expressions.number, quantity, setQuantity, 'modal-settings-create-payment-deadline-input-tax', 'modal-settings-create-payment-deadline-legend-quantity-valid', 'modal-settings-create-payment-deadline-legend-quantity-required')
  }

  const executeHideModalSettingUnitCreate = () => {
    modalHide('modal-settings-create-payment-deadline')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValid('modal-settings-create-payment-deadline-legend-type-valid')
    legendIconValidInvalidRestart('modal-settings-create-payment-deadline-input-name', 'modal-settings-create-payment-deadline-legend-quantity-valid')
    legendIconValidInvalidRestart('modal-settings-create-payment-deadline-input-name', 'modal-settings-create-payment-deadline-legend-quantity-required')

    setType({value: '', valid: false})
    setQuantity({value: '', valid: false})
  }

  return (
    <div id="modal-settings-create-payment-deadline" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addDeadlinePaymentParameter}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="row mb-2">
                <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                <div className="col-lg-9">
                  <AppInputDeadlineType component={"modal-settings-create-payment-deadline"} options={null} typeDeadline={type} setTypeDeadline={setType}></AppInputDeadlineType>
                </div>
              </div>
              <div className="row">
                <label className="col-lg-3 col-form-label required">{lang.labels.quantity}</label>
                <div className="col-lg-9">
                  <input id="modal-settings-create-payment-deadline-input-quantity" className="form-control" type="text" autoComplete="off" name="quantity" value={quantity.value} onChange={handleChangeQuantity} onKeyUp={validateQuantity} onBlur={validateQuantity} />
                  <AppLegend component={"modal-settings-create-payment-deadline"} attribute={{check: quantity.valid, name: "quantity", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalSettingUnitCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitSettingsParameterCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalSettingsCreateDeadline;
